import MenuBook from '@mui/icons-material/MenuBook';
import PostAdd from '@mui/icons-material/PostAdd';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { glubesChrome } from '../../theme/createGlubesMuiTheme';
import { getRoleLabel } from '../../utils/roleLabel';
import ThemeToggle from '../ThemeToggle/ThemeToggle';

interface AppLayoutProps {
  children: React.ReactNode;
}

const SIDEBAR_W = 256;
const HEADER_H = 64;

type NavDef = { to: string; label: string; icon: string; match?: (path: string) => boolean };

function AppLayoutInner({ children }: AppLayoutProps) {
  const muiTheme = useTheme();
  const isLgUp = useMediaQuery(muiTheme.breakpoints.up('lg'));
  const { user, clearUser } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const chrome = glubesChrome(muiTheme);

  const displayName = user ? `${user.first_name}, ${getRoleLabel(user.role)}` : '';
  const isManager = user?.role === 'MANAGER';
  const isWriter = user?.role === 'WRITER';

  const libraryAuthorsNav: NavDef[] = [
    {
      to: '/library',
      label: 'Номын сан',
      icon: 'library_books',
      match: (p) => p.startsWith('/library'),
    },
    {
      to: '/authors',
      label: 'Зохиолч',
      icon: 'person',
      match: (p) => p.startsWith('/authors'),
    },
  ];

  const managerNav: NavDef[] = [
    {
      to: '/manager/users',
      label: 'Хэрэглэгчид',
      icon: 'group',
      match: (p) => p.startsWith('/manager/users'),
    },
    {
      to: '/manager/settings',
      label: 'Тохиргоо',
      icon: 'settings',
      match: (p) => p.startsWith('/manager/settings'),
    },
    {
      to: '/library',
      label: 'Номын сан',
      icon: 'library_books',
      match: (p) => p.startsWith('/library'),
    },
    {
      to: '/authors',
      label: 'Зохиолчид',
      icon: 'person',
      match: (p) => p.startsWith('/authors'),
    },
  ];

  const dashboardNav: NavDef = {
    to: '/dashboard',
    label: 'Нүүр',
    icon: 'dashboard',
    match: (p) => p === '/dashboard',
  };

  const profileNav: NavDef = {
    to: '/profile',
    label: 'Профайл',
    icon: 'person',
    match: (p) => p.startsWith('/profile'),
  };

  const path = location.pathname;
  const isDashboardActive = dashboardNav.match?.(path) ?? path === '/dashboard';
  const isProfileActive = profileNav.match?.(path) ?? false;

  const handleLogout = () => {
    localStorage.removeItem('glubesAppToken');
    clearUser();
    navigate('/login', { replace: true });
  };

  const navButtonSx = (active: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2,
    py: 1.5,
    borderRadius: 2,
    textDecoration: 'none',
    color: active ? 'primary.main' : 'text.secondary',
    bgcolor: active ? 'action.selected' : 'transparent',
    borderRight: active ? chrome.navActiveBorder : '2px solid transparent',
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.2em',
    textTransform: 'uppercase' as const,
    transition: 'color 0.2s, background-color 0.2s, transform 0.2s',
    '&:hover': {
      color: 'text.primary',
      bgcolor: 'action.hover',
      transform: 'translateX(4px)',
    },
    ...(active
      ? {
          '&:hover': {
            color: 'primary.main',
            bgcolor: 'action.selected',
            transform: 'none',
          },
        }
      : {}),
  });

  const mobileNavItem = (item: NavDef, active: boolean) => (
    <Box
      key={item.to}
      component={RouterLink}
      to={item.to}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        minWidth: 0,
        flex: 1,
        py: 1,
        textDecoration: 'none',
        color: active ? 'primary.main' : 'text.secondary',
        '&:active': { transform: 'scale(0.96)' },
      }}
    >
      <span
        className="material-symbols-outlined"
        style={active ? { fontVariationSettings: "'FILL' 1, 'wght' 400" } : undefined}
      >
        {item.icon}
      </span>
      <Box component="span" sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.06em' }}>
        {item.label}
      </Box>
    </Box>
  );

  const mobileItems: { def: NavDef; active: boolean }[] = [
    { def: dashboardNav, active: isDashboardActive },
    { def: profileNav, active: isProfileActive },
    ...(isManager
      ? managerNav.slice(0, 3).map((def) => ({
          def,
          active: def.match?.(path) ?? path === def.to,
        }))
      : isWriter
        ? libraryAuthorsNav.map((def) => ({
            def,
            active: def.match?.(path) ?? path === def.to,
          }))
        : []),
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        color: 'text.primary',
      }}
    >
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: (t) => t.zIndex.drawer + 1,
          height: HEADER_H,
          ...(muiTheme.palette.mode === 'dark'
            ? {
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                bgcolor: 'rgba(14,14,14,0.72)',
                borderBottom: 1,
                borderColor: 'divider',
                boxShadow: chrome.headerShadow,
              }
            : {
                bgcolor: 'background.surfaceHigh',
                borderBottom: 'none',
                boxShadow: chrome.headerShadow,
              }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 1,
            px: { xs: 2, sm: 3 },
            maxWidth: 1920,
            mx: 'auto',
            typography: 'body2',
            letterSpacing: '-0.02em',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 } }}>
            <Box
              component={RouterLink}
              to="/dashboard"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                color: 'text.primary',
              }}
            >
              <TypographyBrand />
            </Box>
            {isLgUp && (
              <Box component="nav" sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box
                  component={RouterLink}
                  to="/dashboard"
                  sx={{
                    fontWeight: 700,
                    color: isDashboardActive ? 'primary.main' : 'text.secondary',
                    textDecoration: 'none',
                    borderBottom: isDashboardActive ? 2 : 0,
                    borderColor: 'primary.main',
                    pb: isDashboardActive ? 0.5 : 0,
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  Нүүр
                </Box>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ThemeToggle />
            {/* <IconButton size="small" sx={{ color: 'text.secondary', borderRadius: 2 }} aria-label="Мэдэгдэл">
              <span className="material-symbols-outlined">notifications</span>
            </IconButton>
            <IconButton size="small" sx={{ color: 'text.secondary', borderRadius: 2 }} aria-label="Тусламж">
              <span className="material-symbols-outlined">help</span>
            </IconButton> */}
            <Avatar
              component={RouterLink}
              to="/profile"
              title={displayName || undefined}
              sx={{
                width: 32,
                height: 32,
                ml: 0.5,
                bgcolor: 'background.paper',
                color: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 800,
                border: 1,
                borderColor: chrome.outlineSubtle,
                textDecoration: 'none',
                cursor: 'pointer',
              }}
              aria-label="Профайл руу очих"
            >
              {user?.first_name?.charAt(0)?.toUpperCase() ?? '?'}
            </Avatar>
          </Box>
        </Box>
      </Box>

      {isLgUp && (
        <Box
          component="aside"
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            width: SIDEBAR_W,
            zIndex: (t) => t.zIndex.drawer,
            bgcolor: 'background.nav',
            borderRight: muiTheme.palette.mode === 'dark' ? 1 : 0,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            pt: `${HEADER_H + 16}px`,
            pb: 3,
            px: 2,
            gap: 1,
          }}
        >
          <Box sx={{ px: 2, py: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: 'primary.dark',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                  menu_book
                </span>
              </Box>
              <Box>
                <Box sx={{ fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }}>
                  Tomko Glubes
                </Box>
                <Box
                  sx={{
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {user ? getRoleLabel(user.role) : '—'}
                </Box>
              </Box>
            </Box>
          </Box>

          <Button
            component={RouterLink}
            to="/books/create"
            variant="contained"
            color="primary"
            fullWidth
            sx={{
              mx: 1,
              py: 1.5,
              mb: 2,
              fontWeight: 800,
              boxShadow: 'none',
              ...(muiTheme.palette.mode === 'dark'
                ? { '&:hover': { boxShadow: 2, bgcolor: 'primary.dark' } }
                : { '&:hover': { boxShadow: 'none', filter: 'brightness(0.97)' } }),
            }}
            startIcon={<PostAdd />}
          >
            Шинэ ном
          </Button>

          <Box component="nav" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
            <Box
              component={RouterLink}
              to={dashboardNav.to}
              sx={navButtonSx(isDashboardActive)}
            >
              <span
                className="material-symbols-outlined"
                style={isDashboardActive ? { fontVariationSettings: "'FILL' 1" } : undefined}
              >
                {dashboardNav.icon}
              </span>
              {dashboardNav.label}
            </Box>
            <Box component={RouterLink} to={profileNav.to} sx={navButtonSx(isProfileActive)}>
              <span
                className="material-symbols-outlined"
                style={isProfileActive ? { fontVariationSettings: "'FILL' 1" } : undefined}
              >
                {profileNav.icon}
              </span>
              {profileNav.label}
            </Box>
            {isManager &&
              managerNav.map((item) => {
                const active = item.match?.(path) ?? path === item.to;
                return (
                  <Box key={item.to} component={RouterLink} to={item.to} sx={navButtonSx(active)}>
                    <span
                      className="material-symbols-outlined"
                      style={active ? { fontVariationSettings: "'FILL' 1" } : undefined}
                    >
                      {item.icon}
                    </span>
                    {item.label}
                  </Box>
                );
              })}
            {isWriter &&
              !isManager &&
              libraryAuthorsNav.map((item) => {
                const active = item.match?.(path) ?? path === item.to;
                return (
                  <Box key={item.to} component={RouterLink} to={item.to} sx={navButtonSx(active)}>
                    <span
                      className="material-symbols-outlined"
                      style={active ? { fontVariationSettings: "'FILL' 1" } : undefined}
                    >
                      {item.icon}
                    </span>
                    {item.label}
                  </Box>
                );
              })}
          </Box>

          <Box sx={{ mt: 'auto' }}>
            <Button
              onClick={handleLogout}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                color: 'text.secondary',
                fontSize: '0.6875rem',
                fontWeight: 600,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
              }}
              startIcon={<span className="material-symbols-outlined">logout</span>}
            >
              Гарах
            </Button>
          </Box>
        </Box>
      )}

      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          pt: `${HEADER_H + 32}px`,
          pb: { xs: 10, lg: 6 },
          px: { xs: 2, sm: 3 },
          ml: { lg: `${SIDEBAR_W}px` },
          bgcolor: 'background.surfaceLowest',
        }}
      >
        <Box sx={{ maxWidth: 1280, mx: 'auto', width: 1 }}>{children}</Box>
      </Box>

      {!isLgUp && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (t) => t.zIndex.appBar,
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            ...(muiTheme.palette.mode === 'dark'
              ? {
                  bgcolor: 'rgba(14,14,14,0.92)',
                  backdropFilter: 'blur(16px)',
                  borderTop: 1,
                  borderColor: 'divider',
                }
              : {
                  bgcolor: alpha(
                    muiTheme.palette.background.surfaceHigh ?? muiTheme.palette.background.default,
                    0.92
                  ),
                  backdropFilter: 'blur(16px)',
                  borderTop: 0,
                }),
          }}
        >
          {mobileItems.map(({ def, active }) => mobileNavItem(def, active))}
        </Box>
      )}
    </Box>
  );
}

function TypographyBrand() {
  return (
    <>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <MenuBook sx={{ fontSize: 22 }} />
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: { xs: '1.125rem', sm: '1.25rem' },
          fontWeight: 900,
          letterSpacing: '-0.04em',
          color: 'text.primary',
        }}
      >
        Tomko Glubes
      </Box>
    </>
  );
}

export default function AppLayout({ children }: AppLayoutProps) {
  return <AppLayoutInner>{children}</AppLayoutInner>;
}
